/**
 * 元素 Payload Schema 定义
 * 提供每种元素类型的完整结构规范
 *
 * 系统使用 BlockManager.getBlockByType(type).create(payload) 创建元素
 * 每个 block 的 create 方法会自动合并 defaultData 和传入的 payload
 */

export const ELEMENT_SCHEMAS = `
# 元素 Payload 规范

以下是每种元素的 Payload 格式。注意：
- **type** 使用 "standard-xxx" 格式
- **attributes** 使用 kebab-case 命名
- **children** 是 TextNode 或子元素数组

---

## 1. 文本元素

### 支持的类型
| type | 用途 | 默认样式 |
|------|------|----------|
| standard-text | 通用文本 | 继承 |
| standard-paragraph | 正文段落 | 16px |
| standard-h1 | 主标题 | 40px, 加粗 |
| standard-h2 | 二级标题 | 32px, 加粗 |
| standard-h3 | 三级标题 | 24px, 加粗 |
| standard-h4 | 四级标题 | 18px, 加粗 |

### Payload 格式
\`\`\`json
{
  "type": "standard-paragraph",
  "attributes": {
    "align": "center",
    "color": "#333333",
    "font-size": "16px",
    "font-family": "Arial, sans-serif",
    "font-weight": "normal",
    "line-height": "1.5",
    "padding-top": "10px",
    "padding-bottom": "10px",
    "padding-left": "25px",
    "padding-right": "25px"
  },
  "children": [
    { "text": "普通文本" },
    { "text": "加粗文本", "bold": true },
    { "text": "链接文本", "link": { "href": "https://example.com", "blank": true } }
  ]
}
\`\`\`

### TextNode 属性
| 属性 | 类型 | 说明 |
|------|------|------|
| text | string | 必填，文本内容 |
| bold | boolean | 加粗 |
| italic | boolean | 斜体 |
| underline | boolean | 下划线 |
| strikethrough | boolean | 删除线 |
| color | string | 文字颜色 |
| fontSize | string | 字号（注意：camelCase） |
| fontFamily | string | 字体 |
| link | object | { href: string, blank?: boolean } |

---

## 2. 图片元素

### ⚠️ 重要：width 属性
- **只支持 px 单位**（如 "300px"）或**留空**
- **不支持百分比**（如 "100%" ❌）
- 留空时图片将显示为全宽

### Payload 格式
\`\`\`json
{
  "type": "standard-image",
  "data": {},
  "attributes": {
    "src": "https://example.com/image.jpg",
    "alt": "图片描述（用于 AI 图片生成，需详细）",
    "width": "600px",
    "height": "auto",
    "align": "center",
    "href": "https://example.com",
    "border-radius": "8px",
    "padding-top": "10px",
    "padding-bottom": "10px",
    "padding-left": "0px",
    "padding-right": "0px"
  },
  "children": [{ "text": "" }]
}
\`\`\`

### 全宽图片示例
\`\`\`json
{
  "type": "standard-image",
  "data": {},
  "attributes": {
    "src": "https://images.unsplash.com/photo-xxx",
    "alt": "Modern office workspace with natural lighting",
    "align": "center",
    "padding-top": "0px",
    "padding-bottom": "0px",
    "padding-left": "0px",
    "padding-right": "0px"
  },
  "children": [{ "text": "" }]
}
\`\`\`

---

## 3. 按钮元素

### Payload 格式
\`\`\`json
{
  "type": "standard-button",
  "attributes": {
    "background-color": "#007bff",
    "color": "#FFFFFF",
    "border-radius": "4px",
    "width": "200px",
    "font-size": "16px",
    "font-weight": "bold",
    "align": "center",
    "href": "https://example.com",
    "padding-top": "10px",
    "padding-bottom": "10px"
  },
  "children": [
    { "text": "立即购买" }
  ]
}
\`\`\`

### 带边框按钮
\`\`\`json
{
  "type": "standard-button",
  "attributes": {
    "background-color": "#FFFFFF",
    "color": "#007bff",
    "border-enabled": true,
    "border-color": "#007bff",
    "border-width": "2px",
    "border-radius": "4px",
    "align": "center",
    "padding-top": "10px",
    "padding-bottom": "10px",
    "padding-left": "25px",
    "padding-right": "25px"
  },
  "children": [
    { "text": "了解更多" }
  ]
}
\`\`\`

---

## 4. 分割线元素

### Payload 格式
\`\`\`json
{
  "type": "standard-divider",
  "attributes": {
    "border-color": "#CCCCCC",
    "border-style": "solid",
    "border-width": "1px",
    "width": "100%",
    "align": "center",
    "padding-top": "20px",
    "padding-bottom": "20px"
  },
  "children": []
}
\`\`\`

---

## 5. 间距元素

### Payload 格式
\`\`\`json
{
  "type": "standard-spacer",
  "attributes": {
    "height": "30px"
  },
  "children": []
}
\`\`\`

---

## 6. 导航栏元素

### Payload 格式
\`\`\`json
{
  "type": "standard-navbar",
  "attributes": {
    "align": "center",
    "color": "#333333",
    "font-size": "14px"
  },
  "children": [
    {
      "type": "standard-navbar-link",
      "attributes": { "href": "https://example.com" },
      "children": [{ "text": "首页" }]
    },
    {
      "type": "standard-navbar-link",
      "attributes": { "href": "https://example.com/products" },
      "children": [{ "text": "产品" }]
    },
    {
      "type": "standard-navbar-link",
      "attributes": { "href": "https://example.com/about" },
      "children": [{ "text": "关于" }]
    }
  ]
}
\`\`\`

---

## 7. 社交图标元素

### Payload 格式
\`\`\`json
{
  "type": "standard-social",
  "data": {},
  "attributes": {
    "align": "center",
    "icon-size": "30px",
    "spacing": "20px",
    "padding-top": "0px",
    "padding-bottom": "20px",
    "padding-left": "0px",
    "padding-right": "0px"
  },
  "children": [
    {
      "type": "standard-social-element",
      "data": {},
      "attributes": {
        "src": "https://res.cloudinary.com/.../twitter-icon.png",
        "href": "#",
        "padding-top": "0px",
        "padding-bottom": "0px",
        "padding-left": "0px",
        "padding-right": "0px"
      },
      "children": []
    },
    {
      "type": "standard-social-element",
      "data": {},
      "attributes": {
        "src": "https://res.cloudinary.com/.../facebook-icon.png",
        "href": "#",
        "padding-top": "0px",
        "padding-bottom": "0px",
        "padding-left": "20px",
        "padding-right": "0px"
      },
      "children": []
    },
    {
      "type": "standard-social-element",
      "data": {},
      "attributes": {
        "src": "https://res.cloudinary.com/.../instagram-icon.png",
        "href": "#",
        "padding-top": "0px",
        "padding-bottom": "0px",
        "padding-left": "20px",
        "padding-right": "0px"
      },
      "children": []
    }
  ]
}
\`\`\`

### Social Element 属性说明

**方式 1：使用 src 属性（推荐，直接指定图标图片 URL）**
- \`src\`: 图标图片的 URL
- \`href\`: 链接地址
- 需要显式写出 padding

**方式 2：使用 name 属性（系统自动生成图标）**
- \`name\`: 社交平台名称（facebook, twitter, instagram, linkedin, youtube, pinterest, tiktok, github, dribbble, behance, email, web）
- \`href\`: 链接地址

⚠️ **注意**：如果使用 src，必须提供完整的图标图片 URL。如果使用 name，系统会自动生成对应平台的图标。

---

## 8. Hero 元素

### Payload 格式
\`\`\`json
{
  "type": "standard-hero",
  "attributes": {
    "background-url": "https://example.com/hero-bg.jpg",
    "background-image-enabled": true,
    "background-color": "#000000",
    "height": "400px",
    "mode": "fixed-height",
    "vertical-align": "middle",
    "padding-top": "40px",
    "padding-bottom": "40px"
  },
  "children": [
    {
      "type": "standard-h1",
      "attributes": {
        "align": "center",
        "color": "#FFFFFF",
        "padding-top": "0px",
        "padding-bottom": "15px",
        "padding-left": "0px",
        "padding-right": "0px"
      },
      "children": [{ "text": "欢迎" }]
    },
    {
      "type": "standard-button",
      "attributes": {
        "background-color": "#FF5722",
        "color": "#FFFFFF",
        "align": "center",
        "padding-top": "10px",
        "padding-bottom": "10px",
        "padding-left": "25px",
        "padding-right": "25px"
      },
      "children": [{ "text": "开始探索" }]
    }
  ]
}
\`\`\`

---

## Wrapper（容器）⚠️ 必须包含 type 和 data

### 何时使用 Wrapper

⚠️ **当多个 Section 在视觉上属于同一个容器/分组时，应该使用 Wrapper 包裹它们！**

**使用场景**：
- 多个相关的 Section 在视觉上属于同一个容器
- 例如：两个内容块（顶部和底部）属于同一个视觉分组
- 需要为多个 Section 设置统一的背景色、padding 等样式

**不使用场景**：
- 每个 Section 都是独立的，没有明显的容器关系
- 通常按行（Section）逐个生成即可

### ⚠️ 重要：padding 必须显式写出
Wrapper 的 padding 属性**必须显式声明**，不能省略。

### Payload 格式

\`\`\`json
{
  "type": "standard-wrapper",
  "data": {},
  "attributes": {
    "background-color": "#F5F5F5",
    "padding-top": "20px",
    "padding-bottom": "20px",
    "padding-left": "0px",
    "padding-right": "0px"
  },
  "children": [
    // ⚠️ 注意：Wrapper 的 children 是 Section 数组（不是 Column 数组）
    {
      "type": "standard-section",
      "data": {},
      "attributes": { ... },
      "children": [ /* Column 数组 */ ]
    },
    {
      "type": "standard-section",
      "data": {},
      "attributes": { ... },
      "children": [ /* Column 数组 */ ]
    }
  ]
}
\`\`\`

### 常用属性

- \`background-color\`: 背景色
- \`padding-top\` / \`padding-bottom\` / \`padding-left\` / \`padding-right\`: 内边距（必须显式写出）
- \`border-enabled\`: 是否启用边框
- \`border-width\` / \`border-style\` / \`border-color\`: 边框样式
- \`vertical-align\`: 垂直对齐（top/middle/bottom）

⚠️ **重要区别**：
- **Section** 的 children 是 **Column 数组**
- **Wrapper** 的 children 是 **Section 数组**

---

## Section（区块）⚠️ 必须包含 type 和 data

### ⚠️ 重要：padding 必须显式写出
Section 的 padding 属性**必须显式声明**，不能省略。

### Payload 格式
\`\`\`json
{
  "type": "standard-section",
  "data": {},
  "attributes": {
    "background-color": "#FFFFFF",
    "padding-top": "20px",
    "padding-bottom": "20px",
    "padding-left": "0px",
    "padding-right": "0px"
  },
  "children": [
    // Column 数组（每个 Column 也必须有 type 和 data）
  ]
}
\`\`\`

### 带背景图的 Section
\`\`\`json
{
  "type": "standard-section",
  "data": {},
  "attributes": {
    "background-color": "#000000",
    "background-url": "https://example.com/bg.jpg",
    "background-image-enabled": true,
    "background-repeat": "no-repeat",
    "background-size": "cover",
    "padding-top": "40px",
    "padding-bottom": "40px",
    "padding-left": "0px",
    "padding-right": "0px"
  },
  "children": [...]
}
\`\`\`

---

## Column（列）⚠️ 必须包含 type 和 data

⚠️ **注意：Column 是唯一不需要 padding 的 standard 元素！**

### Payload 格式
\`\`\`json
{
  "type": "standard-column",
  "data": {},
  "attributes": {
    "width": "50%",
    "background-color": "#FFFFFF",
    "vertical-align": "top"
    // 注意：Column 不需要 padding 属性
  },
  "children": [
    // 元素数组（每个元素必须有 type 和 data）
  ]
}
\`\`\`

### 常用宽度组合
| 布局 | 宽度设置 |
|------|----------|
| 单列 | "100%" |
| 两列等宽 | "50%" + "50%" |
| 两列不等宽 | "30%" + "70%" 或 "40%" + "60%" |
| 三列等宽 | "33.33%" + "33.33%" + "33.33%" |

---

## 完整示例

\`\`\`json
{
  "page": {
    "type": "page",
    "attributes": {
      "width": "600px",
      "background-color": "#f5f5f5",
      "content-background-color": "#FFFFFF"
    },
    "data": {
      "breakpoint": "480px",
      "globalAttributes": { "font-family": "Arial, sans-serif" }
    }
  },
  "sections": [
    {
      "type": "standard-section",
      "data": {},
      "attributes": {
        "background-color": "#FFFFFF",
        "padding-top": "20px",
        "padding-bottom": "20px"
      },
      "children": [
        {
          "type": "standard-column",
          "data": {},
          "attributes": { "width": "100%" },
          "children": [
            {
              "type": "standard-image",
              "data": {},
              "attributes": {
                "src": "https://example.com/logo.png",
                "alt": "Company logo",
                "width": "150px",
                "align": "center",
                "padding-top": "0px",
                "padding-bottom": "0px",
                "padding-left": "0px",
                "padding-right": "0px"
              },
              "children": [{ "text": "" }]
            }
          ]
        }
      ]
    },
    {
      "type": "standard-section",
      "data": {},
      "attributes": {
        "background-color": "#007bff",
        "padding-top": "40px",
        "padding-bottom": "40px",
        "padding-left": "0px",
        "padding-right": "0px"
      },
      "children": [
        {
          "type": "standard-column",
          "data": {},
          "attributes": { "width": "100%" },
          "children": [
            {
              "type": "standard-h1",
              "data": {},
              "attributes": {
                "align": "center",
                "color": "#FFFFFF",
                "padding-top": "0px",
                "padding-bottom": "15px",
                "padding-left": "0px",
                "padding-right": "0px"
              },
              "children": [{ "text": "欢迎订阅" }]
            },
            {
              "type": "standard-paragraph",
              "data": {},
              "attributes": {
                "align": "center",
                "color": "#FFFFFF",
                "padding-top": "0px",
                "padding-bottom": "20px",
                "padding-left": "0px",
                "padding-right": "0px"
              },
              "children": [{ "text": "感谢您的关注" }]
            },
            {
              "type": "standard-button",
              "data": {},
              "attributes": {
                "background-color": "#FFFFFF",
                "color": "#007bff",
                "align": "center",
                "href": "https://example.com",
                "padding-top": "10px",
                "padding-bottom": "10px",
                "padding-left": "25px",
                "padding-right": "25px"
              },
              "children": [{ "text": "开始探索" }]
            }
          ]
        }
      ]
    }
  ]
}
\`\`\`
`;

export default ELEMENT_SCHEMAS;
